'use client';

import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslations } from 'next-intl';
import { createContext, type FC, type PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CelebrityShareSuccessModal } from '@/components/molecules/ActionCard/CelebrityShareSuccessModal';
import { LeaderboardShareModal } from '@/components/molecules/ActionCard/LeaderboardShareModal';
import { type LeaderboardSharePreviewRow, shareLeaderboard } from '@/entities/Actions/api/leaderboardShare';
import { notification } from '@/utils/antd-imports';
interface LeaderboardShareModalState {
  referralLink: string;
  shareText: string;
  leaderboardPreview?: LeaderboardSharePreviewRow[];
}
interface LeaderboardShareContextType {
  openLeaderboardShare: () => Promise<void>;
  isShareModalOpen: boolean;
  isRewardModalOpen: boolean;
  closeShareModal: () => void;
  closeRewardModal: () => void;
}
const LeaderboardShareContext = createContext<LeaderboardShareContextType | null>(null);
export const useLeaderboardShare = () => {
  const context = useContext(LeaderboardShareContext);
  if (!context) {
    throw new Error("useLeaderboardShare must be used within LeaderboardShareProvider");
  }
  return context;
};
export const LeaderboardShareProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const tDetail = useTranslations('experiences.detail');
  const tCard = useTranslations('experiences.card');
  const queryClient = useQueryClient();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [modalState, setModalState] = useState<LeaderboardShareModalState | null>(null);
  const [rewardModalData, setRewardModalData] = useState<{
    poe: number;
    coins: number;
  } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [api, contextHolder] = notification.useNotification();
  const closeShareModal = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsShareModalOpen(false);
    setModalState(null);
  }, []);
  const closeRewardModal = useCallback(() => {
    setIsRewardModalOpen(false);
    setRewardModalData(null);
    queryClient.invalidateQueries({
      queryKey: ['user-balance']
    });
  }, [queryClient]);
  const openLeaderboardShare = useCallback(async () => {
    try {
      const response = await shareLeaderboard();
      const {
        reward,
        shareContent
      } = response;
      const previewRow: LeaderboardSharePreviewRow = {
        rank: shareContent.placeNumber,
        displayName: tCard('leaderboard_share_row_my_place'),
        isCurrentUser: true
      };
      setModalState({
        referralLink: shareContent.referralLink,
        shareText: shareContent.shareText,
        leaderboardPreview: [previewRow]
      });
      setIsShareModalOpen(true);
      if (reward.poe > 0 || reward.coins > 0) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          setIsShareModalOpen(false);
          setModalState(null);
          setRewardModalData(reward);
          setIsRewardModalOpen(true);
        }, 10000);
      }
    } catch (error) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      const message = error instanceof AxiosError ? error.response?.status === 400 ? 'Action not available' : error.response?.status === 401 ? 'Unauthorized' : error.response?.status === 404 ? 'No active competition' : undefined : undefined;
      api.error({
        message: message ?? tDetail('shareExperience.error'),
        placement: 'bottom'
      });
    }
  }, [api, tCard, tDetail]);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  return <LeaderboardShareContext.Provider value={{
    openLeaderboardShare,
    isShareModalOpen,
    isRewardModalOpen,
    closeShareModal,
    closeRewardModal
  }}>
      {contextHolder}
      {children}
      <LeaderboardShareModal isOpen={isShareModalOpen} onClose={closeShareModal} shareText={modalState?.shareText ?? ''} referralLink={modalState?.referralLink ?? ''} leaderboardPreview={modalState?.leaderboardPreview} />
      {rewardModalData && <CelebrityShareSuccessModal isOpen={isRewardModalOpen} onClose={closeRewardModal} reward={rewardModalData} />}
    </LeaderboardShareContext.Provider>;
};